import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ClipboardCheck,
  Check,
  X,
  Factory,
  MapPin,
  FileText,
  MailX,
  HelpCircle,
} from "lucide-react";
import type { FeedbackReason } from "@/types/api";
import { getUserMessage } from "@/lib/api-error";

const REASON_CHIPS: {
  value: FeedbackReason;
  labelKey: string;
  icon: React.ElementType;
}[] = [
  {
    value: "wrong_industry",
    labelKey: "feedback.reasonWrongIndustry",
    icon: Factory,
  },
  {
    value: "wrong_location",
    labelKey: "feedback.reasonWrongLocation",
    icon: MapPin,
  },
  {
    value: "not_a_company",
    labelKey: "feedback.reasonNotCompany",
    icon: FileText,
  },
  {
    value: "poor_data_quality",
    labelKey: "feedback.reasonPoorData",
    icon: MailX,
  },
  { value: "other", labelKey: "feedback.reasonOther", icon: HelpCircle },
];

export interface FeedbackState {
  signal: "relevant" | "not_relevant";
  reason?: string;
}

interface FeedbackButtonsProps {
  resultId: string;
  feedbackState?: FeedbackState;
  onFeedback: (
    resultId: string,
    signal: "relevant" | "not_relevant",
    reason?: string,
    note?: string,
  ) => Promise<boolean>;
  /** Show full-width chip labels instead of compact icons */
  expanded?: boolean;
  /** Match action button sizing (h-11, full width) */
  fullWidth?: boolean;
}

export function FeedbackButtons({
  resultId,
  feedbackState,
  onFeedback,
  expanded,
  fullWidth,
}: FeedbackButtonsProps) {
  const { t } = useTranslation("results");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [step, setStep] = useState<"ask" | "reason">("ask");
  const [selectedReason, setSelectedReason] = useState<FeedbackReason | null>(
    null,
  );
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Already submitted — show final state
  if (feedbackState) {
    if (feedbackState.signal === "relevant") {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-success">
          <Check className="h-3.5 w-3.5" /> {t("feedback.stateRelevant")}
        </span>
      );
    }
    const reasonChip = REASON_CHIPS.find(
      (c) => c.value === feedbackState.reason,
    );
    return (
      <Badge variant="destructive" className="text-xs gap-1 font-medium">
        <X className="h-3 w-3" />
        {reasonChip ? t(reasonChip.labelKey) : t("feedback.stateNotRelevant")}
      </Badge>
    );
  }

  const handlePopoverOpenChange = (open: boolean) => {
    setPopoverOpen(open);
    if (!open) {
      setStep("ask");
      setSelectedReason(null);
      setNote("");
      setError(null);
    }
  };

  const handleRelevant = async () => {
    setSubmitting(true);
    setError(null);
    const ok = await onFeedback(resultId, "relevant");
    setSubmitting(false);
    if (!ok) {
      setError(getUserMessage("FEEDBACK_FAILED"));
    } else {
      setPopoverOpen(false);
    }
  };

  const handleChipClick = async (reason: FeedbackReason) => {
    if (reason === "other") {
      setSelectedReason("other");
      return;
    }
    setSubmitting(true);
    setError(null);
    const ok = await onFeedback(resultId, "not_relevant", reason);
    setSubmitting(false);
    if (!ok) {
      setError(getUserMessage("FEEDBACK_FAILED"));
    } else {
      setPopoverOpen(false);
    }
  };

  const handleOtherSubmit = async () => {
    setSubmitting(true);
    setError(null);
    const ok = await onFeedback(resultId, "not_relevant", "other", note);
    setSubmitting(false);
    if (!ok) {
      setError(getUserMessage("FEEDBACK_FAILED"));
    } else {
      setPopoverOpen(false);
    }
  };

  return (
    <Popover open={popoverOpen} onOpenChange={handlePopoverOpenChange}>
      <PopoverTrigger asChild>
        {expanded ? (
          <Button
            variant="outline"
            size={fullWidth ? "default" : "sm"}
            className={fullWidth ? "h-11 w-full gap-1.5" : "gap-1.5"}
          >
            <ClipboardCheck className="h-3.5 w-3.5" />
            {t("feedback.triggerButton")}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            title={t("feedback.triggerButton")}
          >
            <ClipboardCheck className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-3"
        align="end"
        onClick={(e) => e.stopPropagation()}
      >
        {step === "ask" ? (
          <>
            <p className="text-xs font-medium text-muted-foreground mb-3">
              {t("feedback.askQuestion")}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 text-xs border-success text-success hover:bg-success/10"
                disabled={submitting}
                onClick={handleRelevant}
              >
                {t("feedback.yes")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 text-xs border-destructive text-destructive hover:bg-destructive/10"
                disabled={submitting}
                onClick={() => setStep("reason")}
              >
                {t("feedback.no")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs font-medium text-muted-foreground mb-2">
              {t("feedback.reasonQuestion")}
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {REASON_CHIPS.map((chip) => {
                const Icon = chip.icon;
                return (
                  <button
                    key={chip.value}
                    disabled={submitting}
                    className={`flex items-center gap-1.5 text-xs px-2.5 py-2 rounded-md border transition-colors text-left
                      ${selectedReason === chip.value ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted hover:text-foreground"}
                      ${chip.value === "other" ? "col-span-2" : ""}
                      disabled:opacity-50`}
                    onClick={() => handleChipClick(chip.value)}
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    {t(chip.labelKey)}
                  </button>
                );
              })}
            </div>

            {selectedReason === "other" && (
              <div className="mt-2.5 space-y-2">
                <Input
                  placeholder={t("feedback.notePlaceholder")}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="h-8 text-xs"
                  disabled={submitting}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 flex-1 text-xs"
                    onClick={() => {
                      setSelectedReason(null);
                      setNote("");
                    }}
                  >
                    {t("feedback.cancel")}
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 flex-1 text-xs"
                    onClick={handleOtherSubmit}
                    disabled={submitting}
                  >
                    {t("feedback.submit")}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {error && <p className="text-xs text-destructive mt-2">{error}</p>}
      </PopoverContent>
    </Popover>
  );
}
